import React, { useState } from 'react'
import {
  XMarkIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'

interface CreateHubnaiChargeBoletoModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ChargeResult {
  id: number
  hubnai_charge_id: string
  amount: number
  payment_method: string
  status: string
  boleto_url?: string
  due_date?: string
}

const CreateHubnaiChargeBoletoModal: React.FC<CreateHubnaiChargeBoletoModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [chargeResult, setChargeResult] = useState<ChargeResult | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState({
    amount: '', // Em reais
    description: '',
    customer_name: '',
    customer_email: '',
    customer_document: '',
    due_date: '' // YYYY-MM-DD
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)

      // Converter valor de reais para centavos
      const amountInCents = Math.round(parseFloat(formData.amount) * 100)

      // Preparar dados da cobrança
      const chargeData: any = {
        amount: amountInCents,
        payment_method: 'boleto',
        description: formData.description
      }

      // Adicionar dados do cliente se fornecidos (KYC opcional)
      if (formData.customer_name) {
        chargeData.customer_name = formData.customer_name
      }
      if (formData.customer_email) {
        chargeData.customer_email = formData.customer_email
      }
      if (formData.customer_document) {
        chargeData.customer_document = formData.customer_document.replace(/\D/g, '')
      }
      if (formData.due_date) {
        chargeData.due_date = formData.due_date
      }

      console.log('📤 Criando cobrança Boleto:', chargeData)

      const result = await hubnaiAPI.createCharge(chargeData)

      console.log('✅ Cobrança Boleto criada:', result)

      setChargeResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar cobrança Boleto:', error)
      alert('Erro ao criar cobrança Boleto: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  // Calcular data mínima para vencimento (hoje + 3 dias)
  const getMinDueDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 3)
    return date.toISOString().split('T')[0]
  }

  const handleClose = () => {
    setChargeResult(null)
    setFormData({
      amount: '',
      description: '',
      customer_name: '',
      customer_email: '',
      customer_document: '',
      due_date: ''
    })
    onClose()
  }

  if (!isOpen) return null

  // Se já temos resultado, mostra o boleto
  if (chargeResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Boleto Gerado!</h2>
                  <p className="text-sm text-gray-500">ID: {chargeResult.hubnai_charge_id}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valor:</span>
                <span className="font-semibold text-gray-900">{formatCurrency(chargeResult.amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-orange-700 uppercase">{chargeResult.status}</span>
              </div>
              {chargeResult.due_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Vencimento:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(chargeResult.due_date).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
              {chargeResult.boleto_url && (
                <a
                  href={chargeResult.boleto_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  Ver Boleto PDF
                </a>
              )}
            </div>

            {/* Instrução */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800">
                💡 <strong>Importante:</strong> O pagamento será sincronizado automaticamente após confirmação bancária.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Form para criar cobrança
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <DocumentTextIcon className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Nova Cobrança Boleto</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Not Implemented Warning */}
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Funcionalidade em Desenvolvimento</h3>
                <p className="text-sm text-yellow-800 mb-3">
                  A criação de cobranças via Boleto ainda não está disponível na API Hubnai integrada ao sistema.
                </p>
                <div className="text-xs text-yellow-700">
                  <p className="mb-2"><strong>Métodos disponíveis atualmente:</strong></p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>PIX - Pagamento instantâneo com QR Code</li>
                    <li>Cartão - Crédito e débito com processamento imediato</li>
                  </ul>
                  <p className="mt-3 text-yellow-600">
                    Esta funcionalidade será implementada em breve. Enquanto isso, utilize PIX ou Cartão para processar pagamentos.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Dados da Cobrança - DISABLED */}
          <div className="opacity-50 pointer-events-none">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Dados da Cobrança</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valor (R$) *
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="29.90"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição *
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Ex: Pagamento de serviço"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Vencimento (mínimo 3 dias)
                </label>
                <input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleChange}
                  min={getMinDueDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe em branco para vencimento automático em 3 dias
                </p>
              </div>
            </div>
          </div>

          {/* Dados do Cliente (Opcional) - DISABLED */}
          <div className="opacity-50 pointer-events-none">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dados do Cliente</h3>
            <p className="text-sm text-gray-500 mb-4">Opcional - Preencha para vincular a um cliente</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="João da Silva"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="joao@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF/CNPJ
                </label>
                <input
                  type="text"
                  name="customer_document"
                  value={formData.customer_document}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="000.000.000-00"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Fechar
            </button>
            <button
              type="button"
              disabled={true}
              className="flex-1 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-medium flex items-center justify-center gap-2"
              title="Funcionalidade não disponível"
            >
              <DocumentTextIcon className="w-5 h-5" />
              Não Disponível
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateHubnaiChargeBoletoModal
